/**
 * BlockModel - High-level API for building and transforming 3D block models
 */

import type { Block, Vec3, Vec4 } from "./types";
import { Mat4 } from "./math";

export class BlockModel {
  private blocks: Block[] = [];

  /**
   * Add a single block to the model
   */
  addBlock(block: Block): this {
    this.blocks.push(block);
    return this;
  }

  /**
   * Add multiple blocks to the model
   */
  addBlocks(blocks: Block[]): this {
    this.blocks.push(...blocks);
    return this;
  }

  /**
   * Scale all blocks uniformly
   */
  scale(factor: number): this {
    this.blocks = this.blocks.map((b) => ({
      ...b,
      center: [
        b.center[0] * factor,
        b.center[1] * factor,
        b.center[2] * factor,
      ] as Vec3,
      width: b.width * factor,
      height: b.height * factor,
      depth: b.depth * factor,
    }));
    return this;
  }

  /**
   * Scale blocks with separate factors for each axis
   */
  scaleXYZ(x: number, y: number, z: number): this {
    this.blocks = this.blocks.map((b) => ({
      ...b,
      center: [b.center[0] * x, b.center[1] * y, b.center[2] * z] as Vec3,
      width: b.width * x,
      height: b.height * y,
      depth: b.depth * z,
    }));
    return this;
  }

  /**
   * Translate all blocks
   */
  translate(x: number, y: number, z: number): this {
    this.blocks = this.blocks.map((b) => ({
      ...b,
      center: [b.center[0] + x, b.center[1] + y, b.center[2] + z] as Vec3,
    }));
    return this;
  }

  /**
   * Rotate blocks around Y axis
   */
  rotateY(angle: number): this {
    const rotMatrix = Mat4.rotateY(Mat4.identity(), angle);
    this.blocks = this.blocks.map((b) => ({
      ...b,
      center: Mat4.transformVec3(rotMatrix, b.center),
    }));
    return this;
  }

  /**
   * Rotate blocks around X axis
   */
  rotateX(angle: number): this {
    const rotMatrix = Mat4.rotateX(Mat4.identity(), angle);
    this.blocks = this.blocks.map((b) => ({
      ...b,
      center: Mat4.transformVec3(rotMatrix, b.center),
    }));
    return this;
  }

  /**
   * Rotate blocks around Z axis
   */
  rotateZ(angle: number): this {
    const rotMatrix = Mat4.rotateZ(Mat4.identity(), angle);
    this.blocks = this.blocks.map((b) => ({
      ...b,
      center: Mat4.transformVec3(rotMatrix, b.center),
    }));
    return this;
  }

  /**
   * Mirror blocks across the X axis
   * Creates a symmetrical copy on the opposite side
   */
  mirrorX(): this {
    const mirrored = this.blocks.map((b) => ({
      ...b,
      center: [-b.center[0], b.center[1], b.center[2]] as Vec3,
    }));
    this.blocks = [...this.blocks, ...mirrored];
    return this;
  }

  /**
   * Mirror blocks across the Y axis
   */
  mirrorY(): this {
    const mirrored = this.blocks.map((b) => ({
      ...b,
      center: [b.center[0], -b.center[1], b.center[2]] as Vec3,
    }));
    this.blocks = [...this.blocks, ...mirrored];
    return this;
  }

  /**
   * Mirror blocks across the Z axis
   */
  mirrorZ(): this {
    const mirrored = this.blocks.map((b) => ({
      ...b,
      center: [b.center[0], b.center[1], -b.center[2]] as Vec3,
    }));
    this.blocks = [...this.blocks, ...mirrored];
    return this;
  }

  /**
   * Get the current block definitions
   */
  getBlocks(): Block[] {
    return [...this.blocks];
  }
}

/**
 * Helper function to create a block with simplified parameters
 */
export function createBlock(
  x: number,
  y: number,
  z: number,
  width: number,
  height: number,
  depth: number,
  color: Vec4
): Block {
  return {
    center: [x, y, z],
    width,
    height,
    depth,
    color,
  };
}
